import {
  ArrowRight,
  CakeSlice,
  Check,
  Clock3,
  Gift,
  Heart,
  MapPin,
  Sparkles,
  Truck,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import PublicNavbar from "../components/PublicNavbar";
import { fetchPublicMonthlySpecials } from "../../specials/monthlySpecialsPublicApi";
import { fetchPublicMenu } from "../storefront/menu/menuPublicApi";
import "./landing-page.css";

const fallbackFeaturedItems = [
  {
    id: "fallback-cupcakes",
    name: "Cupcakes",
    category: "Cupcakes",
    description:
      "Moist cupcakes finished with rich frosting and premium ingredients.",
    image: "/img/image15.jpeg",
  },
  {
    id: "fallback-pockets",
    name: "Pie Pockets / Dessert Lumpia",
    category: "Handheld Desserts",
    description:
      "Golden handheld desserts filled with warm, fruity goodness.",
    image: "/img/image2.jpeg",
  },
  {
    id: "fallback-pies",
    name: 'Pies (9")',
    category: "Pies",
    description:
      "Classic pies made with timeless flavors and baked fresh to order.",
    image: "/img/image4.jpeg",
  },
  {
    id: "fallback-cheesecakes",
    name: "Cheesecakes",
    category: "Cheesecakes",
    description:
      "Creamy handcrafted cheesecake made in the flavor your heart desires.",
    image: "/img/image13.jpeg",
  },
];

function fallbackImageForItem(item = {}) {
  const text = normalizedText(
    `${item.category_name || ""} ${item.name || ""}`
  );

  if (
    text.includes("pie pocket") ||
    text.includes("dessert lumpia") ||
    text.includes("lumpia")
  ) {
    return "/img/image2.jpeg";
  }

  if (text.includes("cheesecake")) {
    return "/img/image7.jpeg";
  }

  if (text.includes("cupcake")) {
    return "/img/image15.jpeg";
  }

  if (text.includes("pie")) {
    return "/img/image8.jpeg";
  }

  return "/img/image15.jpeg";
}

function getMenuItemImage(item = {}) {
  const image =
    item.image_url ||
    item.image ||
    item.photo_url ||
    item.photo ||
    item.primary_image?.image_url ||
    item.primary_image?.url ||
    item.images?.[0]?.image_url ||
    item.images?.[0]?.url;
  return image || fallbackImageForItem(item);
}

const landingCategoryMatchers = ["cupcake", "pie pocket", "dessert lumpia", "lumpia", "cheesecake", "pie"];

function normalizedText(value = "") {
  return String(value).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function isLandingMenuItem(item) {
  const text = normalizedText(`${item.category_name || ""} ${item.name || ""}`);
  return landingCategoryMatchers.some((matcher) => text.includes(normalizedText(matcher)));
}

function specialLinkedMenuItemIds(specials) {
  return new Set(
    specials.flatMap((special) => (special.items || []).map((item) => item.menu_item).filter(Boolean)),
  );
}


function LandingPage() {
  const [menuItems, setMenuItems] = useState([]);
  const [featuredSpecials, setFeaturedSpecials] = useState([]);

  useEffect(() => {
    const controller = new AbortController();
    async function loadFeaturedItems() {
      try {
        const [specials, items] = await Promise.all([
          fetchPublicMonthlySpecials({ featured: true, signal: controller.signal }),
          fetchPublicMenu({ signal: controller.signal }),
        ]);
        setFeaturedSpecials(specials);
        setMenuItems(items);
      } catch (err) {
        if (err.name !== "AbortError") {
          setFeaturedSpecials([]);
          setMenuItems([]);
        }
      }
    }
    loadFeaturedItems();
    return () => controller.abort();
  }, []);

  const featuredCards = useMemo(() => {
    const specials = featuredSpecials.map((special) => ({
      id: `special-${special.id}`,
      name: special.name,
      category: special.badge_text || "Monthly Special",
      description:
        special.short_description ||
        special.description ||
        "Limited-time sweets from the SugaLumps kitchen.",
      image: special.image_url || "/img/image15.jpeg",
    }));

    const linkedIds = specialLinkedMenuItemIds(featuredSpecials);

    const regularItems = menuItems
      .filter((item) => item.is_active !== false && item.is_available !== false)
      .filter((item) => !linkedIds.has(item.id))
      .filter(isLandingMenuItem)
      .sort(
        (a, b) =>
          Number(b.is_featured === true) -
            Number(a.is_featured === true) ||
          Number(a.display_order || 0) -
            Number(b.display_order || 0)
      )
      .map((item) => ({
        id: `menu-${item.id}`,
        name: item.name,
        category: item.category_name || "Menu Favorite",
        description:
          item.short_description ||
          item.description ||
          "Handcrafted sweets made to order.",
        image: getMenuItemImage(item),
      }));

    const combined = [...specials, ...regularItems];

    const hasCupcakes = combined.some((item) =>
      normalizedText(`${item.category || ""} ${item.name || ""}`).includes(
        "cupcake"
      )
    );

    if (!hasCupcakes) {
      combined.unshift({
        id: "fallback-cupcakes",
        name: "Cupcakes",
        category: "Cupcakes",
        description:
          "Moist cupcakes finished with rich frosting and premium ingredients.",
        image: "/img/image15.jpeg",
      });
    }

    return combined.slice(0, 6);
  }, [featuredSpecials, menuItems]);

  return (
    <div className="sugalumps-home">
      <PublicNavbar />

      <main>
        <section className="sugalumps-hero">
          <div className="sugalumps-hero__image">
            <img
              src="/img/image15.jpeg"
              alt="Chocolate cupcakes topped with hazelnut chocolates"
            />

            <div className="sugalumps-hero__overlay" />
          </div>

          <div className="sugalumps-hero__content">
            <div className="sugalumps-hero__eyebrow">
              <Sparkles size={16} />
              Black woman-owned bakery
            </div>

            <h1>
              Desserts made to
              <span> be remembered.</span>
            </h1>

            <p>
              Gourmet cupcakes, handcrafted pie pockets, decadent
              cheesecakes, and sweet treats delivered throughout
              Montgomery County and the surrounding DMV area.
            </p>

            <div className="sugalumps-hero__actions">
              <Link
                to="/menu"
                className="sugalumps-button sugalumps-button--primary"
              >
                Explore the Menu
                <ArrowRight size={18} />
              </Link>

              <a
                href="#featured"
                className="sugalumps-button sugalumps-button--light"
              >
                View Favorites
              </a>
            </div>

            <div className="sugalumps-hero__details">
              <span>
                <Truck size={17} />
                Delivery only
              </span>

              <span>
                <MapPin size={17} />
                Silver Spring, Maryland
              </span>

              <span>
                <Clock3 size={17} />
                Two weeks&apos; notice
              </span>
            </div>
          </div>

          <div className="sugalumps-hero__floating-card">
            <span className="sugalumps-hero__floating-icon">
              <Heart size={21} />
            </span>

            <div>
              <small>Made with care</small>
              <strong>Fresh, handcrafted sweets</strong>
            </div>
          </div>
        </section>

        <section className="sugalumps-trust-strip">
          <div className="sugalumps-trust-strip__inner">
            <div>
              <span>
                <CakeSlice size={20} />
              </span>

              <div>
                <strong>Handcrafted</strong>
                <small>Made to order</small>
              </div>
            </div>

            <div>
              <span>
                <Sparkles size={20} />
              </span>

              <div>
                <strong>Gourmet flavors</strong>
                <small>Classic and creative</small>
              </div>
            </div>

            <div>
              <span>
                <Truck size={20} />
              </span>

              <div>
                <strong>Delivery only</strong>
                <small>Serving the DMV</small>
              </div>
            </div>

            <div>
              <span>
                <Gift size={20} />
              </span>

              <div>
                <strong>Celebration ready</strong>
                <small>Perfect for special moments</small>
              </div>
            </div>
          </div>
        </section>

        <section
          className="sugalumps-section sugalumps-featured"
          id="featured"
        >
          <div className="sugalumps-section-heading">
            <div>
              <span className="sugalumps-kicker">
                Customer favorites
              </span>

              <h2>Something sweet for every craving.</h2>
            </div>

            <div className="sugalumps-section-heading__side">
              <p>
                Explore a collection of handcrafted desserts made
                with bold flavor, careful detail, and plenty of love.
              </p>

              <Link to="/menu" className="sugalumps-text-link">
                View the full menu
                <ArrowRight size={17} />
              </Link>
            </div>
          </div>

          <div className="sugalumps-featured-grid">
            {featuredCards.map((item) => (
              <article
                className="sugalumps-product-card"
                key={item.id}
              >
                <Link
                  to="/menu"
                  className="sugalumps-product-card__image"
                  aria-label={`View ${item.name} on the menu`}
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = fallbackImageForItem({
                        name: item.name,
                        category_name: item.category,
                      });
                    }}
                  />

                  <span className="sugalumps-product-card__category">
                    {item.category}
                  </span>
                </Link>

                <div className="sugalumps-product-card__body">
                  <h3>{item.name}</h3>
                  <p>{item.description}</p>

                  <Link
                    to="/menu"
                    className="sugalumps-product-card__link"
                  >
                    View options
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section
          className="sugalumps-section sugalumps-about"
          id="about"
        >
          <div className="sugalumps-about__image">
            <img
              src="/img/image0.jpeg"
              alt="Chocolate cupcake topped with a peanut butter cup"
            />

            <div className="sugalumps-about__image-card">
              <strong>Made for the moment</strong>
              <span>
                Birthdays, celebrations, gifts, events, and everyday
                cravings.
              </span>
            </div>
          </div>

          <div className="sugalumps-about__content">
            <span className="sugalumps-kicker">Our story</span>

            <h2>A family name turned into something sweet.</h2>

            <p>
              SugaLumps Cupcakery &amp; Sweet Treats is a Black
              woman-owned business based in Montgomery County,
              Maryland, proudly serving customers throughout the DMV.
            </p>

            <p>
              The name “SugaLumps” comes from a special nickname the
              owner&apos;s great-uncle gave to all of his nieces. That
              family memory now lives on through gourmet cupcakes,
              handheld desserts, cheesecakes, pies, and custom sweets
              made for every kind of celebration.
            </p>

            <ul className="sugalumps-check-list">
              <li>
                <span>
                  <Check size={15} />
                </span>
                Gourmet cupcakes and custom flavors
              </li>

              <li>
                <span>
                  <Check size={15} />
                </span>
                Pie pockets and dessert lumpia
              </li>

              <li>
                <span>
                  <Check size={15} />
                </span>
                Cheesecakes, pies, cakes, and dessert boxes
              </li>
            </ul>

            <Link
              to="/menu"
              className="sugalumps-button sugalumps-button--dark"
            >
              Discover Our Desserts
              <ArrowRight size={18} />
            </Link>
          </div>
        </section>

        <section
          className="sugalumps-delivery"
          id="delivery"
        >
          <div className="sugalumps-delivery__media">
            <img
              src="/img/image2.jpeg"
              alt="SugaLumps handheld dessert lumpia"
            />

            <div className="sugalumps-delivery__media-label">
              <Truck size={19} />
              Delivered to your door
            </div>
          </div>

          <div className="sugalumps-delivery__content">
            <span className="sugalumps-kicker">
              Delivery information
            </span>

            <h2>Fresh desserts, delivered with care.</h2>

            <p>
              SugaLumps currently offers delivery throughout
              Montgomery County and the surrounding DMV area.
            </p>

            <div className="sugalumps-delivery__facts">
              <div>
                <span>
                  <MapPin size={19} />
                </span>

                <div>
                  <strong>Based in Silver Spring</strong>
                  <small>
                    Serving Montgomery County and surrounding areas
                  </small>
                </div>
              </div>

              <div>
                <span>
                  <Truck size={19} />
                </span>

                <div>
                  <strong>Free local delivery</strong>
                  <small>
                    Within a 30-minute radius of Silver Spring
                  </small>
                </div>
              </div>

              <div>
                <span>
                  <Clock3 size={19} />
                </span>

                <div>
                  <strong>Advance ordering</strong>
                  <small>
                    Please order at least two weeks in advance
                  </small>
                </div>
              </div>
            </div>

            <Link
              to="/menu"
              className="sugalumps-button sugalumps-button--primary"
            >
              Start Your Order
              <ArrowRight size={18} />
            </Link>
          </div>
        </section>

        <section className="sugalumps-cta">
          <div className="sugalumps-cta__background">
            <img
              src="/img/image11.jpeg"
              alt=""
              aria-hidden="true"
            />
            <div />
          </div>

          <div className="sugalumps-cta__content">
            <span className="sugalumps-kicker">
              Make the moment sweeter
            </span>

            <h2>Planning a celebration or special event?</h2>

            <p>
              From birthdays and office events to showers, gifts, and
              custom dessert tables, SugaLumps can help you create
              something unforgettable.
            </p>

            <div className="sugalumps-cta__actions">
              <Link
                to="/menu"
                className="sugalumps-button sugalumps-button--light"
              >
                Browse the Menu
                <ArrowRight size={18} />
              </Link>

              <a
                href="#contact"
                className="sugalumps-button sugalumps-button--outline-light"
              >
                Contact SugaLumps
              </a>
            </div>
          </div>
        </section>
      </main>

    </div>
  );
}

export default LandingPage;